<template>
    <div class="mobile-form-container">
        <branding-header />

        <h3 class="form-title mt-2 mb-1 primary--text">Review & Confirm Order Info</h3>
        <p class="text-center mb-4">
            Please verify all information is correct
        </p>

        <v-form
            ref="confirmationForm"
            v-model="form_valid"
        >
            <div class="mobile-info-item">
                <div class="info-row">
                    <span class="label">Name:</span>
                    <span class="value">{{ order_data.first_name }} {{ order_data.last_name }}</span>
                </div>
            </div>

            <div class="mobile-info-item">
                <div class="info-row">
                    <span class="label">Email:</span>
                    <span class="value">{{ order_data.email }}</span>
                </div>
            </div>

            <div class="mobile-info-item">
                <div class="info-row">
                    <span class="label">Phone:</span>
                    <span class="value">{{ order_data.phone }}</span>
                </div>
            </div>

            <div class="mobile-info-item mb-4">
                <div class="info-row">
                    <span class="label">Billing & Shipping Address:</span>
                </div>
                <div class="address-content">
                    <div>{{ order_data.shipping_address.line1 }}</div>
                    <div v-if="order_data.shipping_address.line2">{{ order_data.shipping_address.line2 }}</div>
                    <div>{{ order_data.shipping_address.city }}, {{ order_data.shipping_address.state }} {{
                        order_data.shipping_address.zip }}</div>
                </div>
            </div>

            <div class="text-center mb-3">
                <v-btn
                    class="check-all-btn"
                    color="primary"
                    text
                    small
                    @click="checkAll"
                >
                    <v-icon
                        aria-hidden="true"
                        left
                        small
                    >mdi-check-all</v-icon>
                    Check All
                </v-btn>
            </div>

            <div class="confirmations-section mb-6">
                <v-checkbox
                    v-model="confirmations.name_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    :rules="[rules.required]"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Name</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.email_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    :rules="[rules.required]"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Email</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.phone_correct"
                    class="mobile-checkbox"
                    hide-details="auto"
                    :rules="[rules.required]"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Phone</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.shipping_correct"
                    class="mobile-checkbox"
                    color="primary"
                    :rules="[rules.required]"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label"><strong>Billing & Shipping Address</strong> is correct</span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.age_verification"
                    class="mobile-checkbox mb-1"
                    color="primary"
                    :rules="[rules.required]"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label">
                            I understand that to receive my package, someone over the age of 21 must be home
                            to sign for the package
                        </span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.email_consent"
                    class="mobile-checkbox consent-checkbox"
                    color="primary"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label">
                            I give Barrel Creek Vineyards or third-parties acting on its behalf permission to email me with order updates, info and offers.
                            Use is subject to <a
                                href="#"
                                @click.prevent.stop="showTerms"
                            >Terms</a> with an arbitration clause and a <a
                                href="#"
                                @click.prevent.stop="showPrivacy"
                            >Privacy Policy</a>.
                        </span>
                    </template>
                </v-checkbox>

                <v-checkbox
                    v-model="confirmations.text_consent"
                    class="mobile-checkbox consent-checkbox"
                    color="primary"
                    hide-details="auto"
                    dense
                >
                    <template #label>
                        <span class="checkbox-label">
                            I give Barrel Creek Vineyards or third-parties acting on its behalf permission to send text messages
                            with info, promotions and offers to the number above, including use of an automated
                            system. Consent is not a condition of purchase. Texts are recorded. Message and/or data
                            rates may apply. Use is subject to <a
                                href="#"
                                @click.prevent.stop="showTerms"
                            >Terms</a> with an arbitration clause and a <a
                                href="#"
                                @click.prevent.stop="showPrivacy"
                            >Privacy Policy</a>.
                        </span>
                    </template>
                </v-checkbox>
            </div>

            <transition name="fade">
                <v-alert
                    v-if="show_submit_error"
                    type="error"
                    class="mb-3"
                    dense
                >
                    Use the "Check All" button above or ensure all required boxes are checked before submitting.
                </v-alert>
            </transition>

            <div class="mobile-actions mt-2">
                <v-btn
                    color="primary"
                    class="action-btn"
                    outlined
                    block
                    @click="showRequestChangesInfo"
                >
                    <v-icon left>mdi-pencil</v-icon>
                    <strong>Request Changes</strong>
                </v-btn>

                <v-btn
                    color="primary"
                    class="action-btn submit-btn"
                    block
                    @click="submitConfirmation"
                >
                    <v-icon left>mdi-check</v-icon>
                    <strong>Confirm Details</strong>
                </v-btn>
            </div>
        </v-form>

        <placeholder-dialog ref="placeholderDialog" />
    </div>
</template>

<script>
import BrandingHeader from '@/components/demos/shared/branding-header.vue';
import PlaceholderDialog from '@/components/demos/shared/placeholder-dialog.vue';

export default {
    name: 'OrderConfirmationForm',

    components: {
        BrandingHeader,
        PlaceholderDialog
    },

    props: {
        order_data: {
            type: Object,
            required: true
        },

        allow_change_request: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            form_valid: false,
            show_submit_error: false,
            confirmations: {
                name_correct: false,
                email_correct: false,
                phone_correct: false,
                email_consent: false,
                text_consent: false,
                shipping_correct: false,
                age_verification: false
            },
            rules: {
                required: value => !!value || 'This confirmation is required'
            }
        }
    },

    computed: {
        canSubmit() {
            const required_confirmations = [
                'name_correct',
                'email_correct',
                'phone_correct',
                'shipping_correct',
                'age_verification',
            ];

            return required_confirmations.every(key => this.confirmations[key]);
        }
    },

    methods: {
        checkAll() {
            this.confirmations.name_correct = true;
            this.confirmations.email_correct = true;
            this.confirmations.phone_correct = true;
            this.confirmations.shipping_correct = true;
            this.confirmations.age_verification = true;
            this.confirmations.email_consent = true;
            this.confirmations.text_consent = true;

            this.show_submit_error = false;
        },

        needToChange() {
            if (this.allow_change_request) {
                this.$emit('need-change');
            }
        },

        submitConfirmation() {
            if (this.canSubmit) {
                this.show_submit_error = false;
                this.$emit('submitted', {
                    confirmations: this.confirmations,
                    has_email_consent: this.confirmations.email_consent,
                    has_text_consent: this.confirmations.text_consent
                });
            } else {
                this.show_submit_error = true;
            }
        },

        showTerms() {
            this.$refs.placeholderDialog.show('Terms & Conditions');
        },

        showPrivacy() {
            this.$refs.placeholderDialog.show('Privacy Policy');
        },

        showRequestChangesInfo() {
            this.$refs.placeholderDialog.show('', `We'll cover this scenario shortly! For now, click "Confirm Details" with all boxes checked, or use "Next" below to simulate this.`);
        }
    }
}
</script>

<style lang="scss" scoped>
.mobile-info-item {
    background: #f8f9fa;
    border-radius: 0.375rem;
    padding: 0.75rem;
    border: 1px solid #dee2e6;
    margin-bottom: 0.5rem;

    .info-row {
        display: flex;
        align-items: center;
        text-align: left;

        .label {
            font-weight: 600;
            color: #555;
            margin-right: .25rem;
            font-size: 0.8rem;
        }

        .value {
            color: #333;
            font-size: 0.8rem;
            word-break: break-word;
        }
    }

    .address-content {
        text-align: left;
        line-height: 1.3;
        font-size: 0.8rem;
        color: #333;
    }
}

.check-all-btn {
    width: 100%;
    background-color: var(--v-secondary-base);
}

.confirmations-section {
    display: flex;
    flex-direction: column;
    gap: .5rem;
}
</style>